<template>
  <div class="equipment-list min-h-screen bg-gradient-to-br from-green-50 to-cyan-100 p-4 md:p-6">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 class="text-2xl md:text-3xl font-bold text-gray-800">Equipment Management</h1>
          <p class="text-gray-600 mt-1">Manage your farm equipment efficiently</p>
        </div>
        <button
          @click="handleAddEquipment"
          class="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Equipment
        </button>
      </div>

      <!-- Filters -->
      <div v-if="showFilters" class="mb-6">
        <EquipmentFilters @filter="handleFilter" />
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <LoadingSpinner class="mx-auto" />
        <p class="text-gray-600 mt-2">Loading equipment...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div class="flex items-center">
          <span class="text-red-600">⚠️</span>
          <p class="text-red-800 ml-2">{{ error }}</p>
        </div>
        <button @click="fetchEquipment" class="btn-secondary mt-2">Try Again</button>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="filteredEquipment.length === 0"
        class="text-center py-12 bg-gray-50 rounded-lg"
      >
        <p class="text-gray-600 mb-4">No equipment found</p>
        <button @click="handleAddEquipment" class="btn-primary">Add Your First Equipment</button>
      </div>

      <!-- Equipment Table (CRUD) -->
      <div v-else class="overflow-x-auto rounded-lg shadow-sm border border-gray-200 bg-white/70">
        <!-- Rows per page selector -->
        <div class="flex justify-end items-center p-2">
          <label class="mr-2 text-sm text-gray-700">Rows per page:</label>
          <select
            class="border rounded px-2 py-1"
            :value="rowsPerPage"
            @change="handleRowsPerPageChange"
          >
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
          </select>
        </div>
        <table class="min-w-full divide-y divide-gray-200 bg-white/50">
          <thead class="bg-gray-100/80">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                #
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                Name
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                Type
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                Model
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                Serial
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                Fuel
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                Purchase Date
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                Created By
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                Owned By
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                Created At
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(item, idx) in paginatedEquipment"
              :key="item.id"
              class="hover:bg-gray-50/70 transition-colors"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ (currentPage - 1) * rowsPerPage + idx + 1 }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ item.name }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{{ item.type }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{{ item.model }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {{ item.serial_number }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {{ item.requires_fuel ? item.fuel_type || 'Yes' : 'No' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="{
                    'bg-green-100 text-green-800': item.status === 'active',
                    'bg-gray-100 text-gray-800': item.status === 'inactive',
                    'bg-yellow-100 text-yellow-800': item.status === 'maintenance',
                  }"
                  class="px-2 py-1 text-xs font-medium rounded-full"
                >
                  {{ item.status }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {{ item.purchase_date }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {{ item.created_by || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {{ item.owned_by || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {{ item.created_at ? new Date(item.created_at).toLocaleString() : '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  @click="handleEdit(item)"
                  class="text-blue-600 hover:text-blue-900 mr-3 transition-colors"
                  title="Edit Equipment"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
                <button
                  @click="handleView(item)"
                  class="text-green-600 hover:text-green-900 mr-3 transition-colors"
                  title="View Equipment"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </button>
                <button
                  @click="handleDelete(item.id)"
                  class="text-red-600 hover:text-red-900 transition-colors"
                  title="Delete Equipment"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <!-- Pagination controls -->
        <div class="flex justify-between items-center p-2 border-t bg-gray-50">
          <button
            class="btn-secondary"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
          >
            Previous
          </button>
          <span class="text-sm text-gray-700">Page {{ currentPage }} of {{ totalPages }}</span>
          <button
            class="btn-secondary"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
          >
            Next
          </button>
        </div>
      </div>

      <!-- Pagination -->
      <div class="mt-4 flex flex-col md:flex-row items-center justify-between">
        <div class="text-sm text-gray-700">
          Showing
          <select
            v-model="rowsPerPage"
            @change="handleRowsPerPageChange"
            class="ml-1 mr-1 p-1 border border-gray-300 rounded"
          >
            <option v-for="option in [5, 10, 25, 50]" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
          entries per page
        </div>
        <div class="flex items-center">
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="btn-pagination"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 18l-6-6 6-6"
              />
            </svg>
          </button>
          <span class="mx-2 text-gray-700"> Page {{ currentPage }} of {{ totalPages }} </span>
          <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === totalPages"
            class="btn-pagination"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 6l6 6-6 6"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Equipment Form Modal -->
      <EquipmentForm
        v-if="showForm"
        :equipment="editingEquipment"
        :loading="formLoading"
        :showForm="showForm"
        @saved="handleFormSaved"
        @cancel="handleFormCancel"
      />

      <!-- Delete Confirmation Modal -->
      <ConfirmDialog
        :visible="showDeleteConfirm"
        title="Delete Equipment"
        message="Are you sure you want to delete this equipment? This action cannot be undone."
        @confirm="confirmDelete"
        @cancel="showDeleteConfirm = false"
      />

      <!-- Notification -->
      <div
        v-if="notification"
        class="fixed top-4 right-4 bg-green-100 text-green-800 px-4 py-2 rounded shadow"
      >
        {{ notification }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { equipmentApi } from '@/api/equipmentApi'
import type { Equipment } from '@/types/equipment'
import EquipmentFilters from './EquipmentFilters.vue'
import EquipmentForm from './EquipmentForm.vue'
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'

const equipment = ref<Equipment[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const showForm = ref(false)
const showFilters = ref(false)
const showDeleteConfirm = ref(false)
const editingEquipment = ref<Equipment | null>(null)
const deletingEquipmentId = ref<number | null>(null)
const formLoading = ref(false)
interface EquipmentFilters {
  status?: 'active' | 'inactive' | 'maintenance'
  requires_fuel?: boolean
  type?: string
  search?: string
}

const currentFilters = ref<Partial<EquipmentFilters>>({})
const notification = ref<string>('')

// Pagination state
const rowsPerPage = ref(10)
const currentPage = ref(1)

// Fetch equipment from API
const fetchEquipment = async () => {
  loading.value = true
  error.value = null
  try {
    equipment.value = await equipmentApi.getAll()
  } catch (err: unknown) {
    error.value = err instanceof Error ? err.message : 'Failed to fetch equipment'
  } finally {
    loading.value = false
  }
}

onMounted(fetchEquipment)

const router = useRouter()

const filteredEquipment = computed(() => {
  // Ensure we always start with an array to avoid runtime errors when API returns paginated/object shapes.
  let filtered: Equipment[] = Array.isArray(equipment.value) ? equipment.value : []
  if (currentFilters.value.status) {
    filtered = filtered.filter((eq) => eq.status === currentFilters.value.status)
  }
  if (currentFilters.value.requires_fuel !== undefined) {
    filtered = filtered.filter((eq) => eq.requires_fuel === currentFilters.value.requires_fuel)
  }
  if (currentFilters.value.type) {
    filtered = filtered.filter((eq) => eq.type === currentFilters.value.type)
  }
  if (currentFilters.value.search) {
    const searchTerm = currentFilters.value.search.toLowerCase()
    filtered = filtered.filter(
      (eq) =>
        eq.name.toLowerCase().includes(searchTerm) ||
        eq.model.toLowerCase().includes(searchTerm) ||
        eq.serial_number.toLowerCase().includes(searchTerm),
    )
  }
  return filtered
})

const sortedEquipment = computed(() => {
  const list = Array.isArray(filteredEquipment.value) ? filteredEquipment.value.slice() : []
  return list.sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })
})

const paginatedEquipment = computed(() => {
  const start = (currentPage.value - 1) * rowsPerPage.value
  const end = start + rowsPerPage.value
  const list = Array.isArray(sortedEquipment.value) ? sortedEquipment.value : []
  return list.slice(start, end)
})

const totalPages = computed(() => {
  return Math.ceil(sortedEquipment.value.length / rowsPerPage.value) || 1
})

const handleFilter = (filters: Partial<EquipmentFilters>) => {
  currentFilters.value = filters
}

const handleAddEquipment = () => {
  editingEquipment.value = null
  showForm.value = true
}

const handleEdit = (equip: Equipment) => {
  editingEquipment.value = equip
  showForm.value = true
}

const handleView = (equip: Equipment) => {
  // Navigate to the equipment detail route
  if (equip && equip.id != null) {
    router.push({ name: 'EquipmentDetail', params: { id: String(equip.id) } })
  } else {
    console.log('View equipment:', equip)
  }
}

const handleDelete = (id: number) => {
  deletingEquipmentId.value = id
  showDeleteConfirm.value = true
}

const confirmDelete = async () => {
  // allow id === 0 by checking against null
  if (deletingEquipmentId.value != null) {
    try {
      await equipmentApi.delete(deletingEquipmentId.value)
      showDeleteConfirm.value = false
      deletingEquipmentId.value = null
      notification.value = 'Equipment deleted successfully!'
      fetchEquipment()
      setTimeout(() => (notification.value = ''), 2000)
    } catch (err) {
      error.value = 'Failed to delete equipment.'
      notification.value = 'Failed to delete equipment.'
      setTimeout(() => (notification.value = ''), 2000)
      console.error('Failed to delete equipment:', err)
    }
  }
}

const handleFormSaved = () => {
  notification.value = 'Equipment saved successfully!'
  showForm.value = false
  editingEquipment.value = null
  fetchEquipment()
  setTimeout(() => (notification.value = ''), 2000)
}

function handleFormCancel() {
  notification.value = 'Action cancelled.'
  showForm.value = false
  setTimeout(() => (notification.value = ''), 1500)
}

function goToPage(page: number) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

function handleRowsPerPageChange(event: Event) {
  rowsPerPage.value = parseInt((event.target as HTMLSelectElement).value, 10)
  currentPage.value = 1
}
</script>
